<template>
  <div v-loading="loading" class="space-y-2">
    <div class="brand-card-lg">
      <div>
        <div class="flex items-center space-x-6">
          <img src="@/assets/img/slack-icon.svg" class="h-8 inline" />
          <span class="title">Connect To Slack</span>
        </div>
      </div>
      <div class="mt-1 pl-16" v-loading="loadingSlackConnect" v-can-enable="'cf:write'">
        <p class="subtitle mt-3 text-brand-brown text-lg font-medium">Link a Slack channel for live messages about saved customers.</p>
        <div v-if="slackConnection" class="mt-4">
          <div class="flex space-x-4 items-center">
            <a :href="slackConnection.incoming_webhook.configuration_url" target="_blank" id="manage-slack-trigger">
              <el-button type="primary" size="medium">Manage Slack Connection</el-button>
            </a>
            <el-button type="primary" @click="removeSlackConnection" size="medium">Remove Slack Connection</el-button>
            <el-button type="text" @click="testSlackConnection" size="medium">Test Connection</el-button>
          </div>

          <p class="font-semibold mt-6">Manage Slack Notifications</p>
          <p class="text-sm text-gray-600 mt-1">Receive notifications when:</p>
          <div v-if="org && org.notifications" class="space-y-2 divide-y divide-brand-sand mt-3">
            <div
              v-for="noti in slackNotificationOptions"
              :key="noti.key"
              class="flex items-center justify-between pt-2"
              :id="`slack-noti-${noti.key}`"
            >
              <p class="text-gray-500 text-sm">{{ noti.label }}</p>
              <el-switch :value="org.notifications.slack[noti.key]" @change="(v) => updateSlackNotifications(noti.key, v)"></el-switch>
            </div>
          </div>
        </div>
        <div v-else class="mt-4">
          <a :href="slackOAuthUrl" id="connect-slack-trigger"
            ><img
              alt="Add to Slack"
              height="40"
              width="139"
              src="https://platform.slack-edge.com/img/add_to_slack.png"
              srcset="https://platform.slack-edge.com/img/add_to_slack.png 1x, https://platform.slack-edge.com/img/add_to_slack@2x.png 2x"
          /></a>
        </div>
      </div>
    </div>
    <div class="brand-card-lg">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-teal-200" />
          <font-awesome-icon :icon="['fas', 'envelope-open']" class="text-teal-600 opacity-50 text-2xl" />
        </font-awesome-layers>
        <span class="title">Email Reports</span>
      </div>
      <div class="mt-4 pl-16">
        <div>
          <p class="font-semibold">Manage Your Email Reports</p>
          <p class="text-sm text-gray-600 mt-1">Churnkey will send analytics reports for the selected intervals:</p>
          <div v-if="org && org.notifications" class="space-y-2 divide-y divide-brand-sand mt-3">
            <div
              v-for="noti in emailNotificationOptions"
              :key="noti.key"
              class="flex items-center justify-between pt-2"
              :id="`email-noti-${noti.key}`"
            >
              <p class="text-sm">
                <b>{{ noti.title }}</b>
                {{ noti.label }}
              </p>
              <el-switch
                v-can-enable="'cf:write'"
                :value="org.notifications.email[noti.key]"
                @change="(v) => updateEmailNotifications(noti.key, v)"
              ></el-switch>
            </div>
          </div>
        </div>
      </div>
    </div>
    <div class="brand-card-lg">
      <div class="flex items-center space-x-4">
        <font-awesome-layers class="text-5xl">
          <font-awesome-icon :icon="['fas', 'circle']" class="text-fuschia-200" />
          <font-awesome-icon :icon="['fas', 'server']" class="text-fuschia-600 opacity-50 text-2xl" />
        </font-awesome-layers>
        <!-- <font-awesome-icon :icon="['fas', 'server']" class="text-xl text-brand-gray-blue" /> -->
        <span class="title">Webhooks</span>
      </div>
      <div class="mt-4 pl-16">
        <div>
          <p class="font-semibold">Manage Incoming Webhooks</p>
          <p class="text-sm text-gray-600 mt-1">
            Create webhook endpoints, so that Churnkey can notify your integration when asynchronous events occur.
            <a href="https://docs.churnkey.co/webhooks" target="_blank" class="text-brand-blue font-semibold"> Learn more → </a>
          </p>
          <webhook-manager></webhook-manager>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import AuthService from '@/api/auth.service';
import * as actions from '@/store/action-types';
import * as mutations from '@/store/mutation-types';
import { mapGetters } from 'vuex';
import orgService from '@/api/org.service';
import WebhookManager from './WebhookManager.vue';

const slackNotificationOptions = [
  { key: 'cancel', label: '… a customer cancels their subscription' },
  { key: 'pause', label: '… a customer pauses their subscription' },
  { key: 'discount', label: '… a customer accepts a discount instead of cancelling' },
  { key: 'abort', label: '… a customer aborts the cancellation flow' },
  { key: 'plan change', label: '… a customer changes their plan' },
  { key: 'trial extension', label: '… a customer extends their trial' },
  { key: 'contact', label: '… a customer contacts support' },
  { key: 'redirect', label: '… a customer is redirected to a different page' },
];

const emailNotificationOptions = [
  { key: 'weekly', title: 'Weekly', label: 'on Monday mornings' },
  { key: 'monthly', title: 'Monthly', label: 'on the 1st of each month' },
];

const clientId = '1286213266306.1977621428370';
const redirectUri = encodeURIComponent(`${window.location.origin}/settings/integrations/slack`.replace('http://', 'https://'));

export default {
  name: 'SettingsIntegrations',
  components: {
    WebhookManager,
  },
  data() {
    return {
      loadingSlackConnect: false,
      slackNotificationOptions,
      emailNotificationOptions,
      loading: true,
    };
  },
  async created() {
    if (this.$route.query.provider === 'slack' && this.$route.query.code && !this.slackConnection) {
      this.loadingSlackConnect = true;
      const slackCode = this.$route.query.code;
      try {
        await AuthService.slackConnect(slackCode, redirectUri);
        await this.$store.dispatch(actions.SYNC_ORG);
        this.loadingSlackConnect = false;
        this.$message.success('Slack connected');
      } catch (e) {
        this.$message.error('Slack connect failed');
        this.loadingSlackConnect = false;
        console.log(e);
      }
    }
    this.loading = false;
  },
  computed: {
    ...mapGetters(['slackConnection', 'org']),
    slackOAuthUrl() {
      return `https://slack.com/oauth/v2/authorize?scope=incoming-webhook&client_id=${clientId}&redirect_uri=${redirectUri}`;
    },
  },
  methods: {
    updateSlackNotifications(key, value) {
      const existingSlackNotifications = this.$store.state?.org?.notifications?.slack || {};
      const updatedSlackNotifications = { ...existingSlackNotifications, ...{ [key]: value } };
      this.$store.commit(mutations.SET_ORG_NOTIFICATIONS, { slack: updatedSlackNotifications });
      this.$store.dispatch(actions.ORG_NOTIFICATIONS_UPDATE);
    },
    updateEmailNotifications(key, value) {
      const existingEmailNotifications = this.$store.state?.org?.notifications?.email || {};
      const updatedEmailNotifications = { ...existingEmailNotifications, ...{ [key]: value } };
      this.$store.commit(mutations.SET_ORG_NOTIFICATIONS, { email: updatedEmailNotifications });
      this.$store.dispatch(actions.ORG_NOTIFICATIONS_UPDATE);
    },
    async testSlackConnection() {
      try {
        await orgService.testSlackConnection(this.org._id);
        this.$message.success('Check your Slack for a test message.');
      } catch (e) {
        this.$message.error('Something went wrong, try reconnecting.');
        console.log(e);
      }
    },
    async removeSlackConnection() {
      console.log('removing');
      try {
        await this.$store.dispatch(actions.REMOVE_SLACK_CONNECTION);
        this.$message.success('Slack connection removed.');
      } catch (e) {
        this.$message.error('Something went wrong, try reconnecting.');
        console.log(e);
      }
    },
  },
};
</script>
